// Captures a poster still from a LOCAL video file before (or while) it
// uploads: object URL → <video> → seek to ~1s (10% of shorter clips) →
// canvas → JPEG. Working on the local file keeps the canvas untainted (no
// CORS) and costs no extra download. Resolves with a File, or null whenever
// capture is not possible — callers skip the poster silently.

const POSTER_MAX_DIMENSION = 1600;
const POSTER_QUALITY = 0.85;
const CAPTURE_TIMEOUT_MS = 15000;

export const canCaptureVideoFrames = () => (
    typeof window !== 'undefined'
    && typeof document !== 'undefined'
    && typeof window.URL?.createObjectURL === 'function'
    && typeof window.HTMLCanvasElement?.prototype?.toBlob === 'function'
    && typeof window.HTMLVideoElement === 'function'
);

// ~1s into the clip skips the black lead-in most videos open on; clips
// shorter than 10s use 10% of their length instead.
export const posterTimeFor = (duration) => (
    Number.isFinite(duration) && duration > 0 ? Math.min(1, duration * 0.1) : 0
);

// Resolves when `event` fires; rejects on a media error or after the timeout.
const waitForEvent = (video, event, timeoutMs) => new Promise((resolve, reject) => {
    let timer = null;
    const cleanup = () => {
        clearTimeout(timer);
        video.removeEventListener(event, onEvent);
        video.removeEventListener('error', onError);
    };
    const onEvent = () => {
        cleanup();
        resolve();
    };
    const onError = () => {
        cleanup();
        reject(new Error('Video could not be decoded'));
    };
    timer = setTimeout(() => {
        cleanup();
        reject(new Error('Timed out reading the video'));
    }, timeoutMs);
    video.addEventListener(event, onEvent);
    video.addEventListener('error', onError);
});

const posterName = (name) => {
    const base = (typeof name === 'string' && name ? name : 'video').replace(/\.[^./\\]+$/, '');
    return `${base}-poster.jpg`;
};

export const captureVideoPoster = async (file, { timeoutMs = CAPTURE_TIMEOUT_MS } = {}) => {
    if (!file || !canCaptureVideoFrames()) {
        return null;
    }

    let objectUrl = null;
    const video = document.createElement('video');
    try {
        objectUrl = window.URL.createObjectURL(file);
        video.muted = true;
        video.playsInline = true;
        video.preload = 'auto';
        const loaded = waitForEvent(video, 'loadeddata', timeoutMs);
        video.src = objectUrl;
        await loaded;

        const target = posterTimeFor(video.duration);
        if (target > 0 && Math.abs(video.currentTime - target) > 0.01) {
            const seeked = waitForEvent(video, 'seeked', timeoutMs);
            video.currentTime = target;
            await seeked;
        }

        const sourceWidth = video.videoWidth;
        const sourceHeight = video.videoHeight;
        if (!sourceWidth || !sourceHeight) {
            return null;
        }
        const scale = Math.min(1, POSTER_MAX_DIMENSION / Math.max(sourceWidth, sourceHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(sourceWidth * scale));
        canvas.height = Math.max(1, Math.round(sourceHeight * scale));
        const context = canvas.getContext('2d');
        if (!context) {
            return null;
        }
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        const blob = await new Promise((resolve) => {
            canvas.toBlob(resolve, 'image/jpeg', POSTER_QUALITY);
        });
        if (!blob || !blob.size) {
            return null;
        }
        return new File([blob], posterName(file.name), { type: 'image/jpeg' });
    } catch {
        return null;
    } finally {
        // Detach the decoder before revoking the blob it reads from.
        video.removeAttribute('src');
        try {
            video.load();
        } catch {
            // jsdom and some browsers refuse load() on a source-less element.
        }
        if (objectUrl) {
            window.URL.revokeObjectURL(objectUrl);
        }
    }
};
